import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { type User, ROLE_LABELS } from '@/types/user';
import { useUserCompanies } from '@/hooks/useUsersData';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { Mail, Phone, Building2, Calendar } from 'lucide-react';

interface ViewUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function ViewUserDialog({ open, onOpenChange, user }: ViewUserDialogProps) {
  // Fetch user companies using the hook that respects demo/live mode
  const { data: allUserCompanies = [], isLoading } = useUserCompanies(user?.id ?? '');

  if (!user) return null;
  
  // Filter to only show companies the user has access to (isLinked = true)
  const userCompanies = allUserCompanies.filter(c => c.isLinked);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'default';
      case 'user':
        return 'secondary';
      case 'view_only':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default' as const;
      case 'suspended':
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'suspended':
        return 'Suspended';
      default:
        return 'Inactive';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Header */}
          <div className="flex items-start gap-4 pb-4 border-b">
            <Avatar className="w-16 h-16">
              <AvatarFallback className="text-lg">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">
                {user.firstName} {user.lastName}
              </h3>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={getRoleBadgeVariant(user.role)}>
                  {ROLE_LABELS[user.role]}
                </Badge>
                <Badge
                  variant={getStatusBadgeVariant(user.status)}
                  className={
                    user.status === 'suspended'
                      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : undefined
                  }
                >
                  {getStatusLabel(user.status)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Contact Information</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{user.email}</span>
              </div>
              {user.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Company Access */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              Company Access ({isLoading ? '...' : userCompanies.length})
            </Label>
            {(() => {
              if (isLoading) {
                return <p className="text-sm text-muted-foreground">Loading companies...</p>;
              }
              if (userCompanies.length > 0) {
                return (
                  <ScrollArea className="max-h-[440px]">
                    <ScrollAreaViewport className="max-h-[440px]">
                      <div className="space-y-2 pr-3">
                        {userCompanies.map((company) => (
                          <div
                            key={company.id}
                            className="flex items-center gap-2 p-2 border rounded-lg"
                          >
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{company.name}</span>
                            <span className="text-xs text-muted-foreground">({company.code})</span>
                          </div>
                        ))}
                      </div>
                    </ScrollAreaViewport>
                    <ScrollAreaScrollbar orientation="vertical" />
                  </ScrollArea>
                );
              }
              return <p className="text-sm text-muted-foreground">No companies assigned</p>;
            })()}
          </div>

          {/* Activity Summary */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Activity Summary</Label>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Created</span>
              </div>
              <p className="text-sm font-medium">
                {format(new Date(user.createdAt), 'MMM d, yyyy')}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
